import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { colors, fonts } from '../brand';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';
import Disclaimer from '../components/Disclaimer';

const sections = [
  {
    heading: 'National Free Legal Help',
    desc: 'Organizations that provide free or low-cost legal assistance to people who cannot afford an attorney.',
    resources: [
      {
        name: 'LawHelp.org',
        url: 'https://www.lawhelp.org',
        desc: 'Find free civil legal aid in your state. Search by state and legal topic to connect with your local legal aid office.',
      },
      {
        name: 'Legal Services Corporation',
        url: 'https://www.lsc.gov/about-lsc/what-legal-aid/get-legal-help',
        desc: 'The largest funder of civil legal aid in the U.S. Their directory connects you to local programs that handle family law cases.',
      },
      {
        name: 'American Bar Association — Free Legal Answers',
        url: 'https://www.americanbar.org/groups/legal_services/flh-home/flh-free-legal-answers/',
        desc: 'Submit civil legal questions online and get answers from volunteer attorneys — free to qualifying low-income users.',
      },
      {
        name: 'Law School Clinics (via NACLE)',
        url: 'https://www.nacle.net',
        desc: 'Law school clinics provide free legal help supervised by faculty attorneys. Many handle family law and divorce cases.',
      },
    ],
  },
  {
    heading: 'Domestic Violence & Safety',
    desc: 'If your divorce involves abuse, coercive control, or safety concerns, these organizations offer specialized legal advocacy.',
    resources: [
      {
        name: 'National Domestic Violence Hotline',
        url: 'https://www.thehotline.org',
        desc: 'Free, confidential support 24/7. Can connect you to local legal advocates and safety planning resources. Call 1-800-799-SAFE (7233) or text START to 88788.',
      },
      {
        name: 'WomensLaw.org',
        url: 'https://www.womenslaw.org',
        desc: 'Plain-language legal information for domestic violence survivors, including divorce and custody. Email hotline available.',
      },
      {
        name: 'Legal Momentum',
        url: 'https://www.legalmomentum.org',
        desc: 'Advances the rights of women and girls through litigation and legal advocacy, with a focus on gender-based violence.',
      },
      {
        name: 'YWCA — Legal Advocacy',
        url: 'https://www.ywca.org/what-we-do/safety-empowerment/',
        desc: 'Many local YWCAs offer free legal advocacy services for survivors. Search your local YWCA for availability.',
      },
    ],
  },
  {
    heading: 'Self-Help Court Resources',
    desc: 'If you\'re going through divorce without an attorney (pro se), these resources help you navigate forms and court procedures.',
    resources: [
      {
        name: 'Self-Help Center Locator (NCSC)',
        url: 'https://www.selfhelpsupport.org/court-locator',
        desc: 'The National Center for State Courts maintains a directory of self-help centers inside courthouses across the country.',
      },
      {
        name: 'USA.gov — Divorce & Family Law',
        url: 'https://www.usa.gov/family-legal-issues',
        desc: 'Federal resource hub with links to state-specific family law forms, court websites, and legal aid locators.',
      },
      {
        name: 'Justia — Family Law',
        url: 'https://www.justia.com/family/divorce/',
        desc: 'Free legal information database covering divorce law, with state-specific guides and links to court self-help resources.',
      },
    ],
  },
  {
    heading: 'Pro Bono Divorce Representation',
    desc: 'Some attorneys take family law cases entirely for free. Here\'s where to find them.',
    resources: [
      {
        name: 'Pro Bono Net',
        url: 'https://www.probono.net',
        desc: 'Connects people with volunteer legal help across the country. Includes a state-by-state directory of pro bono programs.',
      },
      {
        name: 'State Bar Pro Bono Programs',
        url: 'https://www.americanbar.org/groups/legal_services/probono_public_service/directory/',
        desc: 'Every state bar association maintains a pro bono referral service. The ABA directory links to each one.',
      },
      {
        name: 'Volunteer Lawyers Project (your state)',
        url: 'https://www.lawhelp.org',
        desc: 'Most states have a Volunteer Lawyers Project that connects low-income individuals with attorneys willing to take cases for free. Search via LawHelp.org.',
      },
    ],
  },
];

export default function LegalAid() {
  return (
    <div style={styles.page}>
      <Helmet>
        <title>Free Legal Aid for Divorce — Organizations & Resources | Elephants Answering</title>
        <meta name="description" content="Find free legal help for your divorce: legal aid organizations, pro bono attorneys, court self-help centers, and resources for domestic violence survivors." />
      </Helmet>
      <SiteNav />

      <main style={styles.main}>
        <header style={styles.hero}>
          <div style={styles.heroInner}>
            <p style={styles.eyebrow}>FREE RESOURCE</p>
            <h1 style={styles.title}>Free Legal Help for Divorce</h1>
            <p style={styles.subtitle}>
              Navigating the legal system without money is one of the hardest parts of divorce. These organizations exist to help — pro bono attorneys, legal aid offices, court self-help centers, and more.
            </p>
          </div>
        </header>

        <div style={styles.contentInner}>
          <p style={styles.disclaimer}>
            All links are external. Elephants Answering is not affiliated with any organization listed here.
            This is not legal advice — please consult a licensed attorney for guidance specific to your situation.
          </p>

          {sections.map((section) => (
            <section key={section.heading} style={styles.section}>
              <h2 style={styles.sectionHeading}>{section.heading}</h2>
              <p style={styles.sectionDesc}>{section.desc}</p>
              <div style={styles.cards}>
                {section.resources.map((r) => (
                  <a
                    key={r.name}
                    href={r.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.card}
                  >
                    <h3 style={styles.cardName}>{r.name}</h3>
                    <p style={styles.cardDesc}>{r.desc}</p>
                    <span style={styles.cardCta}>Visit site →</span>
                  </a>
                ))}
              </div>
            </section>
          ))}

          <section style={styles.cta}>
            <h2 style={styles.ctaTitle}>Know a resource we missed?</h2>
            <p style={styles.ctaBody}>
              If something helped you, send it our way so it can help the next woman.
            </p>
            <Link to="/contact" style={styles.ctaBtn}>Send a Resource</Link>
          </section>
        </div>
      </main>

      <Disclaimer />
      <SiteFooter />
    </div>
  );
}

const styles = {
  page: { fontFamily: fonts.body, backgroundColor: colors.cream, minHeight: '100vh' },
  main: {},

  hero: { background: `linear-gradient(150deg, ${colors.navy} 0%, #003366 100%)`, padding: '80px 20px' },
  heroInner: { maxWidth: '720px', margin: '0 auto', textAlign: 'center' },
  eyebrow: { fontFamily: fonts.body, color: colors.peach, fontSize: '11px', letterSpacing: '2.5px', textTransform: 'uppercase', marginBottom: '20px' },
  title: { fontFamily: fonts.heading, color: colors.white, fontSize: '40px', lineHeight: '1.3', fontWeight: '600', letterSpacing: '0.5px', marginBottom: '24px' },
  subtitle: { fontFamily: fonts.body, color: 'rgba(255,255,255,0.75)', fontSize: '16px', lineHeight: '1.9' },

  contentInner: { maxWidth: '900px', margin: '0 auto', padding: '64px 20px 80px' },
  disclaimer: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '12px', lineHeight: '1.8', marginBottom: '56px', fontStyle: 'italic', borderLeft: `3px solid ${colors.peach}`, paddingLeft: '16px' },

  section: { marginBottom: '64px' },
  sectionHeading: { fontFamily: fonts.heading, color: colors.navy, fontSize: '22px', fontWeight: '600', letterSpacing: '0.3px', marginBottom: '8px', paddingBottom: '12px', borderBottom: `2px solid ${colors.rose}` },
  sectionDesc: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '14px', lineHeight: '1.8', marginBottom: '24px', fontStyle: 'italic' },

  cards: { display: 'flex', flexDirection: 'column', gap: '16px' },
  card: { display: 'block', padding: '24px 28px', backgroundColor: colors.white, border: `1px solid ${colors.peach}`, borderTop: `3px solid ${colors.peach}`, textDecoration: 'none' },
  cardName: { fontFamily: fonts.heading, color: colors.navy, fontSize: '15px', fontWeight: '600', marginBottom: '8px', letterSpacing: '0.3px' },
  cardDesc: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '13px', lineHeight: '1.8', margin: '0 0 12px' },
  cardCta: { fontFamily: fonts.body, color: colors.rose, fontSize: '12px', fontWeight: '600', letterSpacing: '0.5px' },

  cta: { backgroundColor: colors.navy, padding: '56px 40px', textAlign: 'center', marginTop: '16px' },
  ctaTitle: { fontFamily: fonts.heading, color: colors.white, fontSize: '24px', fontWeight: '600', marginBottom: '12px', letterSpacing: '0.3px' },
  ctaBody: { fontFamily: fonts.body, color: 'rgba(255,255,255,0.7)', fontSize: '15px', lineHeight: '1.8', marginBottom: '28px' },
  ctaBtn: { backgroundColor: colors.rose, color: colors.white, textDecoration: 'none', padding: '14px 36px', fontSize: '14px', fontFamily: fonts.body, fontWeight: '600', letterSpacing: '0.5px', display: 'inline-block' },
};
